import React, { useCallback, useEffect, useMemo, useState } from "react";
import API_BASE_URL from "../config";
import { useAuth0 } from "@auth0/auth0-react";
import "./TripsList.css";

type Trip = {
    id: string;
    name: string;
    tripDate: string;
    tripEndDate: string;
    createdAt: string;
    createdByUserName: string;
};

type TripStatus = "Upcoming" | "Active" | "Completed" | "Dates pending";

export type TripsListProps = {
    getToken: () => Promise<string>;
    onViewDetails: (tripId: string) => void;
};

export function TripsList({
    getToken,
    onViewDetails,
}: TripsListProps) {
    const { user } = useAuth0();

    const [trips, setTrips] = useState<Trip[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [deletingTripId, setDeletingTripId] = useState<string | null>(null);

    const fetchTrips = useCallback(async () => {
        try {
            setError(null);
            setLoading(true);

            const token = await getToken();

            const response = await fetch(`${API_BASE_URL}/trips`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "User-Email": user?.email ?? "",
                },
            });

            if (!response.ok) {
                throw new Error("We could not load your trips.");
            }

            const data: unknown = await response.json();

            if (!Array.isArray(data)) {
                throw new Error("The trips response was not in the expected format.");
            }

            setTrips(data as Trip[]);
        } catch (error: unknown) {
            setError(
                error instanceof Error
                    ? error.message
                    : "An unexpected error occurred while loading your trips."
            );
        } finally {
            setLoading(false);
        }
    }, [getToken, user?.email]);

    useEffect(() => {
        void fetchTrips();
    }, [fetchTrips]);

    async function handleDelete(trip: Trip) {
        const confirmed = window.confirm(
            `Are you sure you want to delete "${trip.name}"? This cannot be undone.`
        );

        if (!confirmed) return;

        try {
            setError(null);
            setDeletingTripId(trip.id);

            const token = await getToken();

            const response = await fetch(`${API_BASE_URL}/trips/${trip.id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 204) {
                setTrips((currentTrips) =>
                    currentTrips.filter((currentTrip) => currentTrip.id !== trip.id)
                );
                return;
            }

            if (response.status === 401 || response.status === 403) {
                setError("You are not authorized to delete this trip.");
                return;
            }

            if (response.status === 404) {
                setError("This trip was not found or has already been deleted.");
                return;
            }

            setError("We could not delete this trip. Please try again.");
        } catch (error: unknown) {
            setError(
                error instanceof Error
                    ? error.message
                    : "An unexpected error occurred while deleting the trip."
            );
        } finally {
            setDeletingTripId(null);
        }
    }

    const summary = useMemo(() => {
        return trips.reduce(
            (result, trip) => {
                const status = getTripStatus(trip);

                result.total += 1;

                if (status === "Upcoming") {
                    result.upcoming += 1;
                }

                if (status === "Active") {
                    result.active += 1;
                }

                return result;
            },
            {
                total: 0,
                upcoming: 0,
                active: 0,
            }
        );
    }, [trips]);

    return (
        <section className="trips-page" aria-labelledby="trips-page-title">
            <div className="trips-hero">
                <div className="trips-hero-glow" aria-hidden="true" />

                <div className="trips-hero-main">
                    <div className="trips-heading">
                        <span className="trips-eyebrow">Trip dashboard</span>

                        <h1 id="trips-page-title">Your trips</h1>

                        <p>
                            Manage shared expenses, review trip details and keep everyone’s
                            balances organized in one place.
                        </p>
                    </div>

                    {user?.email && (
                        <div className="trips-user-card">
                            <span className="trips-user-avatar" aria-hidden="true">
                                {getInitials(user.email)}
                            </span>

                            <div>
                                <span>Signed in as</span>
                                <strong>{user.email}</strong>
                            </div>
                        </div>
                    )}
                </div>

                <div className="trips-summary-grid">
                    <div className="trips-summary-card">
                        <span className="trips-summary-icon" aria-hidden="true">
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                            >
                                <path d="M5 4h14v16H5z" />
                                <path d="M8 2v4M16 2v4M5 9h14" />
                            </svg>
                        </span>

                        <div>
                            <span>Total trips</span>
                            <strong>{summary.total}</strong>
                        </div>
                    </div>

                    <div className="trips-summary-card">
                        <span className="trips-summary-icon" aria-hidden="true">
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                            >
                                <circle cx="12" cy="12" r="8" />
                                <path d="M12 8v4l3 2" />
                            </svg>
                        </span>

                        <div>
                            <span>Upcoming</span>
                            <strong>{summary.upcoming}</strong>
                        </div>
                    </div>

                    <div className="trips-summary-card">
                        <span className="trips-summary-icon" aria-hidden="true">
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                            >
                                <path d="M4 12l5 5L20 6" />
                            </svg>
                        </span>

                        <div>
                            <span>Active now</span>
                            <strong>{summary.active}</strong>
                        </div>
                    </div>
                </div>
            </div>

            {error && (
                <div className="trips-alert" role="alert">
                    <span className="trips-alert-icon" aria-hidden="true">
                        !
                    </span>

                    <div>
                        <strong>Something went wrong</strong>
                        <p>{error}</p>
                    </div>

                    <button
                        type="button"
                        className="trips-alert-button"
                        onClick={() => void fetchTrips()}
                        disabled={loading}
                    >
                        Try again
                    </button>
                </div>
            )}

            {loading ? (
                <TripsLoadingState />
            ) : trips.length === 0 ? (
                <TripsEmptyState />
            ) : (
                <>
                    <div className="trips-desktop-panel">
                        <div className="trips-panel-header">
                            <div>
                                <h2>All trips</h2>
                                <p>
                                    Select a trip to review its participants, expenses and
                                    settlements.
                                </p>
                            </div>

                            <span className="trips-count">
                                {trips.length} {trips.length === 1 ? "trip" : "trips"}
                            </span>
                        </div>

                        <div className="trips-table-wrapper">
                            <table className="trips-table">
                                <thead>
                                    <tr>
                                        <th scope="col">Trip</th>
                                        <th scope="col">Dates</th>
                                        <th scope="col">Created</th>
                                        <th scope="col">Created by</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">
                                            <span className="visually-hidden">Actions</span>
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {trips.map((trip) => {
                                        const status = getTripStatus(trip);
                                        const isDeleting = deletingTripId === trip.id;

                                        return (
                                            <tr key={trip.id}>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="trips-name-button"
                                                        onClick={() => onViewDetails(trip.id)}
                                                    >
                                                        <span className="trips-trip-icon" aria-hidden="true">
                                                            <svg
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="1.8"
                                                            >
                                                                <path d="M5 7h14v12H5z" />
                                                                <path d="M9 7V5h6v2M5 11h14" />
                                                            </svg>
                                                        </span>

                                                        <span>
                                                            <strong>{trip.name}</strong>
                                                            <small>View trip details</small>
                                                        </span>
                                                    </button>
                                                </td>

                                                <td>
                                                    <div className="trips-date-range">
                                                        <strong>{formatDate(trip.tripDate)}</strong>
                                                        <span aria-hidden="true">→</span>
                                                        <strong>{formatDate(trip.tripEndDate)}</strong>
                                                    </div>
                                                </td>

                                                <td>{formatDate(trip.createdAt)}</td>

                                                <td>
                                                    <span className="trips-created-by">
                                                        {trip.createdByUserName || "Unknown"}
                                                    </span>
                                                </td>

                                                <td>
                                                    <TripStatusBadge status={status} />
                                                </td>

                                                <td>
                                                    <div className="trips-actions">
                                                        <button
                                                            type="button"
                                                            className="trips-action-button trips-view-button"
                                                            onClick={() => onViewDetails(trip.id)}
                                                        >
                                                            View
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="trips-action-button trips-delete-button"
                                                            onClick={() => void handleDelete(trip)}
                                                            disabled={isDeleting}
                                                        >
                                                            {isDeleting ? "Deleting..." : "Delete"}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="trips-mobile-list">
                        {trips.map((trip) => {
                            const status = getTripStatus(trip);
                            const isDeleting = deletingTripId === trip.id;

                            return (
                                <article className="trips-mobile-card" key={trip.id}>
                                    <div className="trips-mobile-card-header">
                                        <div className="trips-mobile-title">
                                            <span className="trips-trip-icon" aria-hidden="true">
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.8"
                                                >
                                                    <path d="M5 7h14v12H5z" />
                                                    <path d="M9 7V5h6v2M5 11h14" />
                                                </svg>
                                            </span>

                                            <div>
                                                <h2>{trip.name}</h2>
                                                <span>
                                                    Created {formatDate(trip.createdAt)}
                                                </span>
                                            </div>
                                        </div>

                                        <TripStatusBadge status={status} />
                                    </div>

                                    <div className="trips-mobile-details">
                                        <div>
                                            <span>Start date</span>
                                            <strong>{formatDate(trip.tripDate)}</strong>
                                        </div>

                                        <div>
                                            <span>End date</span>
                                            <strong>{formatDate(trip.tripEndDate)}</strong>
                                        </div>

                                        <div className="trips-mobile-created-by">
                                            <span>Created by</span>
                                            <strong>{trip.createdByUserName || "Unknown"}</strong>
                                        </div>
                                    </div>

                                    <div className="trips-mobile-actions">
                                        <button
                                            type="button"
                                            className="trips-action-button trips-view-button"
                                            onClick={() => onViewDetails(trip.id)}
                                        >
                                            View details
                                            <span aria-hidden="true">→</span>
                                        </button>

                                        <button
                                            type="button"
                                            className="trips-action-button trips-delete-button"
                                            onClick={() => void handleDelete(trip)}
                                            disabled={isDeleting}
                                        >
                                            {isDeleting ? "Deleting..." : "Delete"}
                                        </button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </>
            )}
        </section>
    );
}

function TripStatusBadge({ status }: { status: TripStatus }) {
    const statusClass = status.toLowerCase().replace(/\s+/g, "-");

    return (
        <span className={`trips-status trips-status-${statusClass}`}>
            <span className="trips-status-dot" aria-hidden="true" />
            {status}
        </span>
    );
}

function TripsLoadingState() {
    return (
        <div className="trips-loading" role="status" aria-live="polite">
            <span className="trips-spinner" aria-hidden="true" />

            <div>
                <strong>Loading your trips</strong>
                <p>Please wait while we retrieve the latest information.</p>
            </div>
        </div>
    );
}

function TripsEmptyState() {
    return (
        <div className="trips-empty">
            <span className="trips-empty-icon" aria-hidden="true">
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                >
                    <path d="M5 7h14v12H5z" />
                    <path d="M9 7V5h6v2M8 13h8M12 9v8" />
                </svg>
            </span>

            <span className="trips-eyebrow">No trips yet</span>

            <h2>Start planning your first shared trip</h2>

            <p>
                Use the Start Trip button to create a trip, add participants and begin
                tracking shared expenses.
            </p>
        </div>
    );
}

function parseDate(dateString: string): Date | null {
    if (!dateString || dateString.startsWith("0001")) {
        return null;
    }

    const normalizedDate = /^\d{4}-\d{2}-\d{2}$/.test(dateString)
        ? `${dateString}T00:00:00`
        : dateString;

    const date = new Date(normalizedDate);

    return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(dateString: string): string {
    const date = parseDate(dateString);

    if (!date) {
        return "Not set";
    }

    return date.toLocaleDateString("en-CA", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

function getTripStatus(trip: Trip): TripStatus {
    const startDate = parseDate(trip.tripDate);
    const endDate = parseDate(trip.tripEndDate);

    if (!startDate || !endDate) {
        return "Dates pending";
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    if (today < startDate) {
        return "Upcoming";
    }

    if (today > endDate) {
        return "Completed";
    }

    return "Active";
}

function getInitials(email: string): string {
    const name = email.split("@")[0];

    return name
        .split(/[._-]/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
}